import { FailureMark, toast } from "@foreday/ui/components/toast";
import { createElement, type ReactNode } from "react";
import { NoticeActions } from "@/client/components/notice-toast";

interface ToastLink {
  readonly label: string;
  readonly to: string;
}

export interface Notice {
  /** A notice of the same kind replaces the one showing rather than stacking under it. */
  readonly kind?: string;
  readonly message: string;
  readonly icon?: ReactNode;
  readonly link?: ToastLink;
  readonly undo?: () => Promise<void>;
}

// Read by the keyboard shortcuts that open or take back what the last toasts offered. Undos stack,
// so the key takes back the newest first, even once a newer toast has replaced its own.
let lastLink: ToastLink | null = null;
let undos: (() => void)[] = [];
/** The toast each kind is showing, and how to replace it. */
const showing = new Map<
  string,
  { readonly id: string; readonly replace: () => void }
>();

/** Long enough to read the line and reach Undo; the server allows a few seconds more. */
export const UNDO_FOR = 10_000;

/** Returns a way to take the notice and its undo away, for a write that failed after it showed. */
export function notify(notice: Notice): () => void {
  lastLink = notice.link ?? null;
  const { kind, message, icon, link, undo } = notice;
  // A fresh toast each time: sonner keeps a reused id's shortened timer and merges into one closing.
  const id = crypto.randomUUID();
  const at = Date.now();
  let replaced = false;
  const forgetUndo = () => {
    undos = undos.filter((held) => held !== takeBack);
  };
  const closed = () => {
    // A replaced notice's undo outlives its toast, until its own time is up.
    if (!replaced) forgetUndo();
    if (kind && showing.get(kind)?.id === id) showing.delete(kind);
  };
  // Dismissed on press, so pressing twice, or the button and the key, cannot act twice.
  const takeBack = undo
    ? () => {
        toast.dismiss(id);
        forgetUndo();
        void undo();
      }
    : null;
  if (takeBack) undos.push(takeBack);
  if (kind) {
    showing.get(kind)?.replace();
    showing.set(kind, {
      id,
      replace: () => {
        replaced = true;
        setTimeout(forgetUndo, Math.max(0, at + UNDO_FOR - Date.now()));
        toast.dismiss(id);
      },
    });
  }

  toast(message, {
    id,
    icon,
    duration: undo ? UNDO_FOR : undefined,
    onDismiss: closed,
    onAutoClose: closed,
    action:
      link || takeBack
        ? createElement(NoticeActions, {
            link,
            onUndo: takeBack ?? undefined,
          })
        : undefined,
  });
  return () => {
    forgetUndo();
    toast.dismiss(id);
  };
}

// Retry is offered only when sending again can succeed. A failure that offers it stays until it is
// pressed or closed, so it alone keeps a close button: it is the only place left that names what
// did not save. `retryLabel` names a follow-up rather than a lost write, as Send again on an
// invoice that did issue: its own page offers it too, so the toast leaves after `UNDO_FOR`.
// A failure's line wraps: it ends on what to do, which a cut line would lose.
export function fail(
  message: string,
  retry?: () => void,
  offline = false,
  retryLabel?: string,
): void {
  const id = crypto.randomUUID();
  // Dismissed on press, so pressing twice cannot act twice.
  const sendAgain = retry
    ? () => {
        toast.dismiss(id);
        retry();
      }
    : undefined;
  toast.error(message, {
    id,
    icon: offline ? createElement(FailureMark, { offline }) : undefined,
    duration: retryLabel
      ? UNDO_FOR
      : retry
        ? Number.POSITIVE_INFINITY
        : undefined,
    classNames: { title: "whitespace-normal!" },
    action: retry
      ? createElement(NoticeActions, {
          onRetry: sendAgain,
          retryLabel,
          onClose: () => toast.dismiss(id),
        })
      : undefined,
  });
}

export function hasLastUndo(): boolean {
  return undos.length > 0;
}

export function undoLast(): void {
  undos.at(-1)?.();
}

export function hasLastLink(): boolean {
  return lastLink !== null;
}

/** For the test harness: the last link and undo outlive a render. */
export function forgetLastToast(): void {
  lastLink = null;
  undos = [];
  showing.clear();
}

export function openLastLink(navigate: (to: string) => void): boolean {
  if (!lastLink) return false;
  navigate(lastLink.to);
  return true;
}
